import { InjectionToken } from '@angular/core';

/**
 * Injection token that resolves when Instana early initialization completes.
 *
 * This token is provided by `InstanaLoggingEarlyInitializer` and represents a Promise
 * that resolves once the early initialization phase completes. The early phase includes:
 * - Setting up global error handlers (window.onerror, unhandledrejection)
 * - Loading the Instana EUM script
 * - Applying Instana configuration
 *
 * **Execution Order:**
 * This token ensures that Instana early initialization runs FIRST, before all other
 * initializers. Other initializers (like IdentityInitializer) can depend on this token
 * to guarantee sequential execution.
 *
 * **Usage:**
 * ```typescript
 * {
 *   provide: APP_INITIALIZER,
 *   useFactory: (earlyInstanaInit: Promise<void>) => async () => {
 *     await earlyInstanaInit; // Wait for Instana early init to complete
 *     // ... your initialization code
 *   },
 *   deps: [[new Optional(), INSTANA_EARLY_INIT_COMPLETE]]
 * }
 * ```
 *
 * @see InstanaLoggingEarlyInitializer
 */
export const INSTANA_EARLY_INIT_COMPLETE = new InjectionToken<Promise<void>>(
  'INSTANA_EARLY_INIT_COMPLETE'
);

/**
 * Injection token that resolves when Identity initialization completes.
 *
 * This token is provided by `IdentityInitializer` and represents a Promise that
 * resolves once identity/session initialization completes. Instana late initialization
 * depends on this token to ensure user tracking is set up only after identity is available.
 *
 * **Why defined here?**
 * This token is defined in the instana package (rather than the authorization package)
 * to avoid circular dependencies:
 * - `tswp-core-authorization-implementation` imports from `tswp-core-common-diag-instana`
 *   (for INSTANA_EARLY_INIT_COMPLETE)
 * - If IDENTITY_INIT_COMPLETE were in authorization package, instana would need to import
 *   from authorization, creating a circular dependency
 *
 * **Execution Order:**
 * 1. Instana Early Initialization (no dependencies)
 * 2. Identity Initialization (depends on INSTANA_EARLY_INIT_COMPLETE)
 * 3. Instana Late Initialization (depends on IDENTITY_INIT_COMPLETE)
 *
 * **Usage:**
 * ```typescript
 * {
 *   provide: APP_INITIALIZER,
 *   useFactory: (identityInit: Promise<void>) => async () => {
 *     await identityInit; // Wait for Identity init to complete
 *     // ... your initialization code
 *   },
 *   deps: [[new Optional(), IDENTITY_INIT_COMPLETE]]
 * }
 * ```
 *
 * @see IdentityInitializer
 * @see InstanaLoggingLateInitializer
 */
export const IDENTITY_INIT_COMPLETE = new InjectionToken<Promise<void>>(
  'IDENTITY_INIT_COMPLETE'
);

/**
 * Execution Flow Diagram - Initialization Sequence
 *
 * The initialization system ensures proper sequential execution using Angular's
 * dependency injection with Promise-based tokens. Here's the complete flow:
 *
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │                    MODULE REGISTRATION PHASE                            │
 * │  (Synchronous, happens when module is imported)                        │
 * └─────────────────────────────────────────────────────────────────────────┘
 *                                    │
 *                                    ▼
 *                    ┌───────────────────────────────┐
 *                    │ InstanaLoggingEarlyInitializer│
 *                    │  setupErrorHandlersImmediately│
 *                    │  (synchronous, sets up        │
 *                    │   window.onerror handlers)     │
 *                    └───────────────────────────────┘
 *                                    │
 *                                    ▼
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │                    ANGULAR DEPENDENCY INJECTION PHASE                   │
 * │  (Angular processes providers and resolves dependencies)                 │
 * └─────────────────────────────────────────────────────────────────────────┘
 *                                    │
 *                    ┌───────────────┴───────────────┐
 *                    │                               │
 *                    ▼                               ▼
 *     ┌──────────────────────────┐    ┌──────────────────────────┐
 *     │ Provider 1: Token        │    │ Provider 2: APP_INITIALIZER│
 *     │ INSTANA_EARLY_INIT_      │    │ (depends on token)        │
 *     │ COMPLETE                 │    │                           │
 *     │                          │    │                           │
 *     │ useFactory runs →        │    │ useFactory receives →     │
 *     │ creates Promise →        │    │ same Promise →            │
 *     │ starts initializeEarly()│    │ returns async function    │
 *     └──────────────────────────┘    └──────────────────────────┘
 *                    │                               │
 *                    └───────────────┬───────────────┘
 *                                    │
 *                                    ▼
 * ┌─────────────────────────────────────────────────────────────────────────┐
 * │                    APP_INITIALIZER EXECUTION PHASE                      │
 * │  (Angular calls all APP_INITIALIZER functions and awaits promises)     │
 * └─────────────────────────────────────────────────────────────────────────┘
 *                                    │
 *                    ┌───────────────┴───────────────┐
 *                    │                               │
 *                    ▼                               ▼
 *     ┌──────────────────────────┐    ┌──────────────────────────┐
 *     │ Instana Early Init       │    │ Identity Initializer     │
 *     │                          │    │                          │
 *     │ 1. Error handlers active │    │ 1. Waits for Instana     │
 *     │ 2. Load Instana script   │    │    early init (token)    │
 *     │ 3. Apply Instana config  │    │ 2. Initialize identity  │
 *     │ 4. Promise resolves      │    │ 3. Promise resolves     │
 *     │    → Token available     │    │    → Token available    │
 *     └──────────────────────────┘    └──────────────────────────┘
 *                    │                               │
 *                    └───────────────┬───────────────┘
 *                                    │
 *                                    ▼
 *                    ┌───────────────────────────────┐
 *                    │ Instana Late Initializer       │
 *                    │                                │
 *                    │ 1. Waits for Identity init    │
 *                    │    (token dependency)         │
 *                    │ 2. Sets up user tracking       │
 *                    │ 3. Sets up route tracking      │
 *                    │ 4. Promise resolves            │
 *                    └───────────────────────────────┘
 *                                    │
 *                                    ▼
 *                    ┌───────────────────────────────┐
 *                    │ Angular Application Bootstrap  │
 *                    │ (All initializers complete)    │
 *                    └───────────────────────────────┘
 *
 * Key Points:
 * - Error handlers are set up synchronously before any async code runs
 * - Each phase provides a token that the next phase depends on
 * - The two-provider pattern (token + APP_INITIALIZER) ensures execution
 *   AND enables dependency injection
 * - All promises are awaited before Angular continues bootstrapping
 */
